import { Trans, useTranslation } from "react-i18next";
import cn from "classnames";
import Lottie from "lottie-react";

import { WizardButtons } from "shared/components/WizardNew/components/WizardButtons";
import { WizardData } from "shared/components/WizardNew/types";

import {
  FIRST_VIEW_COOKIE_KEY,
  SESSION_SEEN_COOKIE_KEY,
} from "shared/constants/cookie";
import { SPLIT_TEST } from "shared/constants/split";
import { WEB_PUSH_TYPE } from "shared/constants/webPush";
import { DEFAULT_MODE_WIZARD, SAFE_MODE_WIZARD } from "shared/constants/wizard";

import { simplyClick } from "shared/helpers/analytics";
import { setCookie } from "shared/helpers/cookie";
import { isSplitTestRequired } from "shared/helpers/view";

import { useWizard, WizardStep } from "shared/hooks/useWizard";

import { postWizardFinish } from "shared/services/wizard";

import styles from "@platformAssets/components/Wizard.module.scss";

import welcomeAnimation from "@sharedAssets/lotties/welcome_abroad.json";

type Props = { wizardData: WizardData };

const WelcomeFinish = ({ wizardData }: Props) => {
  const { t } = useTranslation("wizardNew");

  const wizard = useWizard(wizardData);

  const step = wizard[WizardStep.FinishStep];

  const { mode } = wizardData;

  const isSplitReducedWizardEnabled =
    mode === DEFAULT_MODE_WIZARD &&
    isSplitTestRequired(wizard.data, SPLIT_TEST.reducedWizard);

  const handleClick = async () => {
    step.analytics?.start?.forEach((item: string) => {
      simplyClick(item, null, false);
    });

    await postWizardFinish();
    sessionStorage.setItem(WEB_PUSH_TYPE.INACTIVE, "true");
    sessionStorage.setItem(SESSION_SEEN_COOKIE_KEY, "true");
    setCookie(FIRST_VIEW_COOKIE_KEY, true, 364);
    window.location.href = "/app/";
  };

  return (
    <div className={styles.welcomeContainer}>
      <WizardButtons variant="welcome" showBack={false} userData={wizardData} />

      <div className={styles.welcome}>
        <div className={styles.wizardAnimationWrapper}>
          {isSplitReducedWizardEnabled ? (
            <div className={cn(styles.welcomeAnimation, styles.animation1)}>
              {[1, 2, 3].map((row) => (
                <div key={row} className={cn(styles[`row${row}`])} />
              ))}
            </div>
          ) : (
            <Lottie
              animationData={welcomeAnimation}
              loop={true}
              autoplay={true}
              className={styles.wizardFinishAnimationWrapper}
              rendererSettings={{
                preserveAspectRatio: "xMidYMid slice",
              }}
            />
          )}
        </div>

        <div className={styles.wizardInfoWrapper}>
          <div className={styles.welcomeTextWrapper}>
            <div>
              <div
                className={cn(styles.welcomeTitle, {
                  [styles.multiGender]: mode === SAFE_MODE_WIZARD,
                })}
              >
                <Trans
                  t={t}
                  i18nKey="wizardNew:finish.title"
                  values={{
                    userName: wizard?.data?.username,
                  }}
                />
              </div>
              <div
                className={cn(styles.welcomeDescription, {
                  [styles.multiGender]: mode === SAFE_MODE_WIZARD,
                })}
              >
                {mode === SAFE_MODE_WIZARD
                  ? t("finish.descriptionMulti")
                  : t("finish.description")}
              </div>
            </div>
          </div>

          <div className={styles.btnWrap}>
            <button onClick={handleClick} className={styles.welcomeButton}>
              {t("finish.btn")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { WelcomeFinish };
